import { useParams, Link } from 'react-router';
import {
  Loader2,
  ArrowLeft,
  Plus,
  Trash2,
  CheckCircle2,
  AlertCircle,
  LayoutGrid,
  Timer,
  Target,
  Edit2,
  Copy,
  Layers,
  HelpCircle,
  BookOpen,
} from 'lucide-react';
import {
  useEvaluationDetail,
  useAddEvaluationQuestion,
  useUpdateEvaluationQuestion,
  useDeleteEvaluationQuestion,
  useDeleteEvaluation,
} from '../hooks/useEvaluations';
import { Button, StatusChip } from '@lms/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { QuestionType, EvaluationQuestion, EvaluationOption } from '@lms/shared/types';
import { EvaluationModal } from '../components/EvaluationModal';
import { ImportQuestionsModal } from '../components/ImportQuestionsModal';
import { copy } from '../copy/copy';
import { Bilingual } from '../components/Bilingual';
import { useLanguage } from '../lib/LanguageContext';

export function EvaluationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { data: evaluation, isLoading, error } = useEvaluationDetail(id!);
  const addQuestionMutation = useAddEvaluationQuestion();
  const updateQuestionMutation = useUpdateEvaluationQuestion();
  const deleteQuestionMutation = useDeleteEvaluationQuestion();
  const deleteEvaluationMutation = useDeleteEvaluation();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isEditMetadataOpen, setIsEditMetadataOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [newQuestion, setNewQuestion] = useState({
    question: '',
    type: 'MULTIPLE_CHOICE' as QuestionType,
    order: 1,
    points: 10,
    options: [
      { text: '', isCorrect: true, order: 1 },
      { text: '', isCorrect: false, order: 2 },
    ],
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <p className="text-sm font-body text-on-surface-variant">
          <Bilingual {...copy.evaluationDetail.loading} />
        </p>
      </div>
    );
  }

  if (error || !evaluation) {
    return (
      <div className="bg-error-container text-on-error-container p-6 rounded-xl">
        <h3 className="font-headline font-bold mb-2">
          <Bilingual {...copy.evaluationDetail.error} />
        </h3>
        <p className="text-sm font-body">
          {(error as Error).message || copy.evaluationDetail.errorNotFound[language]}
        </p>
        <Link to="/evaluations" className="text-sm font-bold underline mt-4 block">
          <Bilingual {...copy.evaluationDetail.backToBank} />
        </Link>
      </div>
    );
  }

  const handleOpenAdd = () => {
    setEditingQuestionId(null);
    setNewQuestion({
      question: '',
      type: 'MULTIPLE_CHOICE',
      order: evaluation.questions?.length ? evaluation.questions.length + 1 : 1,
      points: 10,
      options: [
        { text: '', isCorrect: true, order: 1 },
        { text: '', isCorrect: false, order: 2 },
      ],
    });
    setIsAddModalOpen(true);
  };

  const handleOpenEdit = (q: EvaluationQuestion) => {
    setEditingQuestionId(q.id);
    setNewQuestion({
      question: q.question,
      type: q.type,
      order: q.order,
      points: q.points,
      options:
        q.options?.map(o => ({ text: o.text, isCorrect: o.isCorrect, order: o.order })) || [],
    });
    setIsAddModalOpen(true);
  };

  const handleAddQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingQuestionId) {
      await updateQuestionMutation.mutateAsync({
        id: editingQuestionId,
        evaluationId: id!,
        ...newQuestion,
        options: newQuestion.options as unknown as EvaluationOption[],
      });
    } else {
      await addQuestionMutation.mutateAsync({
        evaluationId: id!,
        ...newQuestion,
        options: newQuestion.options as unknown as EvaluationOption[],
      });
    }
    setIsAddModalOpen(false);
  };

  const addOption = () => {
    setNewQuestion({
      ...newQuestion,
      options: [
        ...newQuestion.options,
        { text: '', isCorrect: false, order: newQuestion.options.length + 1 },
      ],
    });
  };

  const removeOption = (idx: number) => {
    if (newQuestion.options.length <= 1) return;
    setNewQuestion({
      ...newQuestion,
      options: newQuestion.options.filter((_, i) => i !== idx),
    });
  };

  return (
    <div className="space-y-10 animate-fadeIn">
      <section className="flex flex-col gap-6">
        <Link
          to="/evaluations"
          className="flex items-center gap-2 text-xs font-label font-bold text-on-surface-variant uppercase tracking-widest hover:text-primary transition-colors no-underline group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          <Bilingual {...copy.evaluations.listTitle} />
        </Link>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-8">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <h1 className="text-4xl font-headline font-black text-on-surface tracking-tight m-0 uppercase leading-none italic">
                {evaluation.title}
              </h1>
              <StatusChip
                label={
                  evaluation.type === 'EXAM'
                    ? t(copy.createEvaluation.optionExam)
                    : evaluation.type === 'QUIZ'
                      ? t(copy.createEvaluation.optionQuiz)
                      : evaluation.type
                }
                variant={evaluation.type === 'EXAM' ? 'error' : 'success'}
              />
              <button
                onClick={() => setIsEditMetadataOpen(true)}
                className="p-2 text-on-surface-variant hover:text-primary transition-colors bg-surface-container-low rounded-lg"
              >
                <Edit2 size={16} />
              </button>
            </div>
            <p className="text-lg font-body text-on-surface-variant max-w-3xl leading-relaxed opacity-70">
              {evaluation.description || copy.evaluationDetail.defaultDescription[language]}
            </p>

            {(evaluation.material || evaluation.flow || evaluation.level || evaluation.program) && (
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <span className="text-[9px] font-label font-black text-on-surface-variant uppercase tracking-widest opacity-60">
                  <Bilingual {...copy.evaluationDetail.labelPlacement} />
                </span>
                {evaluation.program &&
                  !evaluation.material &&
                  !evaluation.flow &&
                  !evaluation.level && (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 rounded-full">
                      <BookOpen size={12} className="text-primary" />
                      <span className="text-[10px] font-bold text-primary uppercase tracking-wider">
                        <Bilingual {...copy.evaluationDetail.labelProgram} />{' '}
                        {evaluation.program.name}
                      </span>
                    </div>
                  )}
                {evaluation.level && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary/10 rounded-full">
                    <Layers size={12} className="text-secondary" />
                    <span className="text-[10px] font-bold text-secondary uppercase tracking-wider">
                      <Bilingual {...copy.evaluationDetail.labelLevel} /> {evaluation.level.name}
                    </span>
                  </div>
                )}
                {evaluation.flow && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-tertiary/10 rounded-full">
                    <LayoutGrid size={12} className="text-tertiary" />
                    <span className="text-[10px] font-bold text-tertiary uppercase tracking-wider">
                      <Bilingual {...copy.evaluationDetail.labelFlow} /> {evaluation.flow.name}
                    </span>
                  </div>
                )}
                {evaluation.material && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-container-high rounded-full">
                    <HelpCircle size={12} className="text-on-surface-variant" />
                    <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-wider">
                      <Bilingual {...copy.evaluationDetail.labelMaterial} />{' '}
                      {evaluation.material.title}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-4">
            {id && (
              <Link
                to={`/question-bank?evaluationId=${id}`}
                className="flex h-12 items-center gap-3 rounded-xl bg-surface-container-low px-6 text-xs font-label font-bold uppercase tracking-widest text-on-surface transition-colors hover:bg-surface-container"
              >
                <BookOpen size={16} />
                Pilih dari bank soal
              </Link>
            )}
            <Button
              variant="outline"
              onClick={() => setIsImportModalOpen(true)}
              className="h-12 px-6 rounded-xl flex items-center gap-3 text-xs font-label font-bold uppercase tracking-widest border border-on-surface/10 hover:bg-surface-container"
            >
              <Copy size={16} />
              <Bilingual {...copy.evaluationDetail.btnImportQuestions} />
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsDeleteConfirmOpen(true)}
              className="h-12 px-6 rounded-xl flex items-center gap-3 text-xs font-label font-bold uppercase tracking-widest border border-error/20 text-error hover:bg-error/5"
            >
              <Trash2 size={16} />
              <Bilingual {...copy.evaluationDetail.btnDelete} />
            </Button>
            <Button
              onClick={handleOpenAdd}
              className="h-12 px-8 rounded-xl flex items-center gap-3 text-xs font-label font-bold uppercase tracking-widest shadow-level2"
            >
              <Plus size={18} />
              <Bilingual {...copy.evaluationDetail.btnAddQuestion} />
            </Button>
          </div>
        </div>
      </section>

      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-3 px-6 py-3 bg-surface-container-low border border-on-surface/5 rounded-2xl">
          <Target size={16} className="text-primary" />
          <span className="text-xs font-label font-black text-on-surface-variant uppercase tracking-widest">
            <Bilingual {...copy.evaluationDetail.labelPassing} /> {evaluation.passingScore}%
          </span>
        </div>
        <div className="flex items-center gap-3 px-6 py-3 bg-surface-container-low border border-on-surface/5 rounded-2xl">
          <Timer size={16} className="text-primary" />
          <span className="text-xs font-label font-black text-on-surface-variant uppercase tracking-widest">
            <Bilingual {...copy.evaluationDetail.labelDuration} /> {evaluation.timeLimit ?? '∞'} MIN
          </span>
        </div>
        <div className="flex items-center gap-3 px-6 py-3 bg-surface-container-low border border-on-surface/5 rounded-2xl">
          <LayoutGrid size={16} className="text-primary" />
          <span className="text-xs font-label font-black text-on-surface-variant uppercase tracking-widest">
            <Bilingual {...copy.evaluationDetail.labelTotal} /> {evaluation.questions?.length || 0}{' '}
            <Bilingual {...copy.evaluationDetail.labelQuestionNode} />
          </span>
        </div>
      </div>

      <div className="space-y-6">
        {evaluation.questions?.length === 0 ? (
          <div className="p-24 text-center bg-surface-container-low rounded-3xl border-2 border-dashed border-outline-variant/20 italic">
            <AlertCircle size={48} className="mx-auto text-primary/10 mb-4" />
            <p className="text-on-surface-variant font-body opacity-50">
              <Bilingual {...copy.evaluationDetail.emptyQuestions} />
            </p>
          </div>
        ) : (
          evaluation.questions?.map((q, idx) => (
            <div
              key={q.id}
              className="bg-surface-container-lowest p-8 rounded-3xl border border-on-surface/5 shadow-level1 group hover:border-primary/20 transition-all space-y-6 relative"
            >
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <div className="text-[10px] font-label font-black uppercase tracking-[0.3em] text-primary">
                    <Bilingual {...copy.evaluationDetail.questionHeader} /> {q.order || idx + 1} •{' '}
                    {q.points} PTS
                  </div>
                  <h4 className="text-xl font-headline font-bold text-on-surface m-0 leading-tight">
                    {q.question}
                  </h4>
                  <div className="text-[10px] text-on-surface-variant/60 font-mono tracking-tighter uppercase">
                    {q.type === 'MULTIPLE_CHOICE'
                      ? t(copy.evaluationDetail.optionMultipleChoice)
                      : q.type === 'TRUE_FALSE'
                        ? t(copy.evaluationDetail.optionTrueFalse)
                        : q.type === 'SHORT_ANSWER'
                          ? t(copy.evaluationDetail.optionShortAnswer)
                          : q.type === 'ESSAY'
                            ? t(copy.evaluationDetail.optionEssay)
                            : (q.type as string).replace('_', ' ')}
                  </div>
                </div>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleOpenEdit(q)}
                    className="p-2 text-on-surface-variant hover:text-primary transition-colors bg-surface-container-low rounded-lg"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => deleteQuestionMutation.mutate({ id: q.id, evaluationId: id! })}
                    className="p-2 text-on-surface-variant hover:text-error transition-colors bg-surface-container-low rounded-lg"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {q.type !== 'ESSAY' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {q.options?.map(opt => (
                    <div
                      key={opt.id}
                      className={`flex items-center gap-4 px-5 py-4 rounded-xl border ${opt.isCorrect ? 'bg-success-container/5 border-success/20' : 'bg-surface-container-low border-on-surface/5'}`}
                    >
                      {opt.isCorrect && (
                        <CheckCircle2 size={16} className="text-success shrink-0" />
                      )}
                      <span
                        className={`text-sm font-body ${opt.isCorrect ? 'text-on-success-container font-bold' : 'text-on-surface-variant opacity-80'}`}
                      >
                        {opt.text}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-surface/80 backdrop-blur-sm animate-fadeIn">
          <div className="bg-surface-container-lowest w-full max-w-2xl rounded-3xl shadow-level4 border border-on-surface/5 overflow-hidden flex flex-col max-h-[90vh] animate-scaleIn">
            <div className="p-8 border-b border-on-surface/5 bg-surface-container-low/30">
              <h2 className="text-2xl font-headline font-black text-primary tracking-tight uppercase m-0 leading-none">
                <Bilingual {...copy.evaluationDetail.editorTitle} />
              </h2>
              <p className="text-sm font-body text-on-surface-variant mt-2">
                <Bilingual {...copy.evaluationDetail.editorDesc} />
              </p>
            </div>

            <form onSubmit={handleAddQuestion} className="p-8 overflow-y-auto space-y-8 flex-1">
              <div className="space-y-4">
                <label className="text-[10px] font-label font-black uppercase tracking-widest text-on-surface-variant ml-1">
                  <Bilingual {...copy.evaluationDetail.labelQuestionText} />
                </label>
                <textarea
                  required
                  rows={3}
                  className="w-full bg-surface-container border-none rounded-2xl px-5 py-4 font-body text-sm focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none"
                  placeholder={copy.evaluationDetail.placeholderQuestionText[language]}
                  value={newQuestion.question}
                  onChange={e => setNewQuestion({ ...newQuestion, question: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-label font-black uppercase tracking-widest text-on-surface-variant ml-1">
                    <Bilingual {...copy.evaluationDetail.labelQuestionType} />
                  </label>
                  <select
                    className="w-full bg-surface-container border-none rounded-xl px-4 py-3 font-body text-sm outline-none"
                    value={newQuestion.type}
                    onChange={e => {
                      const type = e.target.value as QuestionType;
                      let options = [...newQuestion.options];

                      if (type === 'TRUE_FALSE') {
                        options = [
                          { text: 'True', isCorrect: true, order: 1 },
                          { text: 'False', isCorrect: false, order: 2 },
                        ];
                      } else if (type === 'SHORT_ANSWER') {
                        options = [{ text: '', isCorrect: true, order: 1 }];
                      } else if (type === 'ESSAY') {
                        options = [];
                      } else if (type === 'MULTIPLE_CHOICE' && options.length === 0) {
                        options = [
                          { text: '', isCorrect: true, order: 1 },
                          { text: '', isCorrect: false, order: 2 },
                        ];
                      }

                      setNewQuestion({ ...newQuestion, type, options });
                    }}
                  >
                    <option value="MULTIPLE_CHOICE">
                      {copy.evaluationDetail.optionMultipleChoice[language]}
                    </option>
                    <option value="TRUE_FALSE">
                      {copy.evaluationDetail.optionTrueFalse[language]}
                    </option>
                    <option value="SHORT_ANSWER">
                      {copy.evaluationDetail.optionShortAnswer[language]}
                    </option>
                    <option value="ESSAY">{copy.evaluationDetail.optionEssay[language]}</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-label font-black uppercase tracking-widest text-on-surface-variant ml-1">
                    <Bilingual {...copy.evaluationDetail.labelQuestionPoints} />
                  </label>
                  <input
                    type="number"
                    className="w-full bg-surface-container border-none rounded-xl px-4 py-3 font-body text-sm outline-none"
                    value={newQuestion.points}
                    onChange={e =>
                      setNewQuestion({ ...newQuestion, points: parseInt(e.target.value) })
                    }
                  />
                </div>
              </div>

              {newQuestion.type !== 'ESSAY' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center px-1">
                    <label className="text-[10px] font-label font-black uppercase tracking-widest text-on-surface-variant">
                      {newQuestion.type === 'SHORT_ANSWER' ? (
                        <Bilingual {...copy.evaluationDetail.labelExpectedAnswer} />
                      ) : (
                        <Bilingual {...copy.evaluationDetail.labelOptions} />
                      )}
                    </label>
                    {(newQuestion.type === 'MULTIPLE_CHOICE' ||
                      newQuestion.type === 'SHORT_ANSWER') && (
                      <button
                        type="button"
                        onClick={addOption}
                        className="text-[10px] font-label font-bold text-primary uppercase hover:underline"
                      >
                        {newQuestion.type === 'SHORT_ANSWER' ? (
                          <Bilingual {...copy.evaluationDetail.btnAddAlternative} />
                        ) : (
                          <Bilingual {...copy.evaluationDetail.btnAddOption} />
                        )}
                      </button>
                    )}
                  </div>

                  <div className="space-y-3">
                    {newQuestion.options.map((opt, idx) => (
                      <div key={idx} className="flex gap-4 items-center animate-fadeIn">
                        <input
                          type={newQuestion.type === 'SHORT_ANSWER' ? 'checkbox' : 'radio'}
                          name="correct-option"
                          checked={opt.isCorrect}
                          onChange={() => {
                            if (newQuestion.type === 'SHORT_ANSWER') {
                              const updated = [...newQuestion.options];
                              updated[idx].isCorrect = !updated[idx].isCorrect;
                              setNewQuestion({ ...newQuestion, options: updated });
                            } else {
                              setNewQuestion({
                                ...newQuestion,
                                options: newQuestion.options.map((o, i) => ({
                                  ...o,
                                  isCorrect: i === idx,
                                })),
                              });
                            }
                          }}
                          className="w-4 h-4 text-primary bg-surface-container border-none focus:ring-0"
                          disabled={newQuestion.type === 'SHORT_ANSWER'}
                        />
                        <input
                          className="flex-1 bg-surface-container border-none rounded-xl px-4 py-3 font-body text-sm outline-none"
                          placeholder={
                            newQuestion.type === 'TRUE_FALSE'
                              ? opt.text === 'True'
                                ? copy.evaluationDetail.placeholderTrue[language]
                                : copy.evaluationDetail.placeholderFalse[language]
                              : newQuestion.type === 'SHORT_ANSWER'
                                ? copy.evaluationDetail.placeholderExpectedAnswer[language]
                                : `${copy.evaluationDetail.optionMultipleChoiceNth[language]} ${idx + 1}`
                          }
                          value={opt.text}
                          readOnly={newQuestion.type === 'TRUE_FALSE'}
                          onChange={e => {
                            const updated = [...newQuestion.options];
                            updated[idx].text = e.target.value;
                            setNewQuestion({ ...newQuestion, options: updated });
                          }}
                        />
                        {(newQuestion.type === 'MULTIPLE_CHOICE' ||
                          newQuestion.type === 'SHORT_ANSWER') && (
                          <button
                            type="button"
                            onClick={() => removeOption(idx)}
                            className="text-on-surface-variant hover:text-error opacity-40 hover:opacity-100"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </form>

            <div className="p-8 border-t border-on-surface/5 flex gap-4 bg-surface-container-low/30">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsAddModalOpen(false)}
                className="flex-1 h-12 rounded-xl text-xs font-label font-bold uppercase tracking-widest"
              >
                <Bilingual {...copy.common.cancel} />
              </Button>
              <Button
                onClick={handleAddQuestion}
                isLoading={addQuestionMutation.isPending}
                className="flex-1 h-12 rounded-xl text-xs font-label font-bold uppercase tracking-widest shadow-level2"
              >
                <Bilingual {...copy.evaluationDetail.btnSaveQuestion} />
              </Button>
            </div>
          </div>
        </div>
      )}

      {isImportModalOpen && id && (
        <ImportQuestionsModal
          currentEvaluationId={id}
          onClose={() => setIsImportModalOpen(false)}
        />
      )}
      {isEditMetadataOpen && evaluation && (
        <EvaluationModal evaluation={evaluation} onClose={() => setIsEditMetadataOpen(false)} />
      )}

      {isDeleteConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-surface/80 backdrop-blur-sm animate-fadeIn">
          <div className="bg-surface-container-lowest w-full max-w-md rounded-3xl shadow-level4 border border-on-surface/5 overflow-hidden animate-scaleIn">
            <div className="p-8 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-error/10 flex items-center justify-center text-error">
                  <AlertCircle size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-headline font-bold text-on-surface m-0">
                    <Bilingual {...copy.evaluationDetail.btnDeleteEval} />
                  </h2>
                  <p className="text-sm font-body text-on-surface-variant mt-1 text-error font-bold">
                    <Bilingual {...copy.evaluationDetail.confirmDeleteActionText} />
                  </p>
                </div>
              </div>
              <p className="text-sm font-body text-on-surface-variant leading-relaxed">
                <Bilingual {...copy.evaluationDetail.confirmDeleteTitle} />{' '}
                <span className="font-bold text-on-surface">&quot;{evaluation.title}&quot;</span>?{' '}
                <Bilingual {...copy.evaluationDetail.confirmDeleteDesc} />
              </p>
              <div className="flex gap-4 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIsDeleteConfirmOpen(false)}
                  className="flex-1 h-12 rounded-xl text-xs font-label font-bold uppercase tracking-widest"
                >
                  <Bilingual {...copy.common.cancel} />
                </Button>
                <Button
                  onClick={() => {
                    deleteEvaluationMutation.mutate(id!, {
                      onSuccess: () => {
                        void navigate('/evaluations');
                      },
                    });
                  }}
                  isLoading={deleteEvaluationMutation.isPending}
                  className="flex-1 h-12 rounded-xl text-xs font-label font-bold uppercase tracking-widest shadow-level2 bg-error text-on-error hover:bg-error/90"
                >
                  <Bilingual {...copy.evaluationDetail.btnDeletePermanently} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
